import { useState, useEffect } from 'react';
import { useParallax } from '@/hooks/useScrollAnimations';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Star } from 'lucide-react';

const words = ['Branding', 'Visibility', 'Voice', 'Identity'];

const Hero = () => {
  const offset = useParallax();
  const [wordIndex, setWordIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[wordIndex];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplayed(word.slice(0, displayed.length + 1));
        if (displayed.length + 1 === word.length) {
          setTimeout(() => setDeleting(true), 1500);
        }
      } else {
        setDisplayed(word.slice(0, displayed.length - 1));
        if (displayed.length - 1 === 0) {
          setDeleting(false);
          setWordIndex((i) => (i + 1) % words.length);
        }
      }
    }, deleting ? 50 : 100);
    return () => clearTimeout(timeout);
  }, [displayed, deleting, wordIndex]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-x-hidden"
    >
      <span id="hero" className="absolute -top-20" />
      {/* Parallax BG */}
      <div
        className="absolute inset-0 bg-secondary"
        style={{ transform: `translateY(${offset * 0.3}px)` }}
      >
        <div className="absolute inset-0 bg-[var(--hero-overlay)]" />
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, hsl(var(--primary) / 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, hsl(var(--primary-glow) / 0.2) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Edge background images */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <div
          className="absolute left-0 top-0 h-[75%] w-1/2"
          style={{
            backgroundImage: 'url(/images/hero-models.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'top right',
            maskImage: 'linear-gradient(to right, rgba(0,0,0,0.4) 0%, transparent 70%), linear-gradient(to top, transparent 0%, rgba(0,0,0,1) 20%)',
            WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0.4) 0%, transparent 70%), linear-gradient(to top, transparent 0%, rgba(0,0,0,1) 20%)',
            maskComposite: 'intersect',
            WebkitMaskComposite: 'destination-in',
          }}
        />
        <div
          className="absolute right-0 top-0 h-[75%] w-1/2"
          style={{
            backgroundImage: 'url(/images/hero-models.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'top left',
            maskImage: 'linear-gradient(to left, rgba(0,0,0,0.4) 0%, transparent 70%), linear-gradient(to top, transparent 0%, rgba(0,0,0,1) 20%)',
            WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.4) 0%, transparent 70%), linear-gradient(to top, transparent 0%, rgba(0,0,0,1) 20%)',
            maskComposite: 'intersect',
            WebkitMaskComposite: 'destination-in',
          }}
        />
      </div>

      {/* Floating elements */}
      <div className="absolute top-1/4 left-[10%] w-3 h-3 bg-primary rounded-full animate-float opacity-60 z-[2]" />
      <div className="absolute top-1/3 right-[15%] w-5 h-5 bg-primary-glow rounded-full animate-float opacity-40 z-[2]" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-1/4 left-[20%] w-4 h-4 bg-primary rounded-full animate-float opacity-50 z-[2]" style={{ animationDelay: '4s' }} />

      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-5 py-2 mb-8"
          style={{ animation: 'slide-up 0.8s ease-out forwards' }}
        >
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-primary-foreground/80 text-sm font-medium">Premium Branding Solutions Since 2012</span>
        </div>

        <h1
          className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-primary-foreground mb-6 font-display leading-tight break-words min-h-[1.2em]"
          style={{ animation: 'slide-up 0.8s ease-out 0.2s both' }}
        >
          Elevate Your{' '}
          <span className="text-primary inline-block min-w-[10ch] text-left align-baseline">
            {displayed || String.fromCharCode(160)}
            <span className="inline-block w-[3px] h-[0.8em] bg-primary ml-1 align-middle"
              style={{ animation: 'typewriter-cursor 1s infinite' }}
            />
          </span>
        </h1>

        <p
          className="text-lg md:text-xl text-primary-foreground/70 max-w-2xl mx-auto mb-10 font-body"
          style={{ animation: 'slide-up 0.8s ease-out 0.4s both' }}
        >
          From school uniforms to corporate branding, we bring your vision to life with
          cutting-edge printing, embroidery, and design solutions.
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          style={{ animation: 'slide-up 0.8s ease-out 0.6s both' }}
        >
          <Button asChild size="lg" className="bg-[#e85a1f] hover:bg-[#d14d15] text-white text-lg px-8 py-6">
            <Link to="/free-mockup">Get Free Mockup <ArrowRight className="w-5 h-5 ml-1" /></Link>
          </Button>
          <Button asChild size="lg" variant="ghost" className="border border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground text-lg px-8 py-6">
            <a href="#quiz">Take 30-Second Quiz</a>
          </Button>
          <Button asChild size="lg" variant="ghost" className="border border-primary-foreground/20 text-primary-foreground/60 hover:bg-primary-foreground/10 hover:text-primary-foreground text-base px-8 py-6">
            <a href="#contact">Get A Quote</a>
          </Button>
        </div>

        {/* Trust indicators */}
        <div
          className="flex items-center justify-center gap-6 mt-12 text-primary-foreground/50 text-sm"
          style={{ animation: 'slide-up 0.8s ease-out 0.8s both' }}
        >
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-primary fill-primary" />
            <span>50+ Schools</span>
          </div>
          <div className="w-px h-4 bg-primary-foreground/20" />
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-primary fill-primary" />
            <span>100+ Corporates</span>
          </div>
          <div className="w-px h-4 bg-primary-foreground/20 hidden sm:block" />
          <div className="hidden sm:flex items-center gap-1.5">
            <Star className="w-4 h-4 text-primary fill-primary" />
            <span>12+ Years</span>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;
